import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Forwarding and follow-me across the account.
 *
 * This is the setting most likely to be wrong and least likely to be noticed.
 * A seat forwarded to a mobile that changed six months ago rings nowhere, and
 * nothing anywhere surfaces it — the caller hears ringing, the owner hears
 * nothing, and everyone assumes the phone is fine. One table listing every
 * forwarded seat is most of the value.
 */

interface Row {
  id: string; number: string; display_name: string; status: string;
  forward_always: string | null;
  forward_busy: string | null;
  forward_no_answer: string | null;
  follow_me: Array<{ destination?: string; delay_sec?: number }> | null;
  do_not_disturb: boolean;
  call_timeout_sec: number;
}

export function ForwardingTab({ tenantId, onOpenExtension }: {
  tenantId: string;
  onOpenExtension?: (id: string) => void;
}) {
  const q = useQuery({
    queryKey: ['forwarding', tenantId],
    queryFn: () => api.get<{ data: Row[] }>(`/v1/tenants/${tenantId}/forwarding`),
    enabled: !!tenantId,
  });

  const rows = q.data?.data ?? [];
  const always = rows.filter((r) => r.forward_always);
  const conditional = rows.filter((r) => !r.forward_always && (r.forward_busy || r.forward_no_answer));
  const followMe = rows.filter((r) => (r.follow_me?.length ?? 0) > 0);
  const untouched = rows.length - always.length - conditional.length;

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <h2 className="arc-h2">Forwarding &amp; follow-me</h2>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
          Where calls go when they do not ring the desk.
        </p>
      </div>

      {q.error && <ErrorBanner error={q.error} />}

      {/* Unconditional forwarding means the desk phone never rings at all.
          That is almost always deliberate and occasionally forgotten, so it
          gets stated rather than left to be spotted in a column. */}
      {always.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {always.length} seat{always.length === 1 ? '' : 's'} forward every call
            and never ring the desk: {always.map((r) => `${r.number} → ${r.forward_always}`).join(', ')}
          </span>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">All seats</h2>
          <span className="arc-subtle">
            {always.length + conditional.length} with forwarding
            {untouched > 0 ? `, ${untouched} without` : ''}
          </span>
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Extension</th>
                <th>Always</th>
                <th>When busy</th>
                <th>No answer</th>
                <th>Follow-me</th>
                <th className="arc-td-num">Rings for</th>
              </tr>
            </thead>
            {q.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={onOpenExtension ? 'arc-clickable' : undefined}
                    onClick={() => onOpenExtension?.(r.id)}>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{r.number}</span>
                        <div className="arc-stack">
                          <span className="arc-truncate">{r.display_name}</span>
                          {r.do_not_disturb && <Badge tone="warn">DND</Badge>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {r.forward_always
                        ? <Badge tone="warn"><span className="arc-mono">{r.forward_always}</span></Badge>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono arc-muted">
                      {r.forward_busy ?? <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono arc-muted">
                      {r.forward_no_answer ?? <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-muted">
                      {(r.follow_me?.length ?? 0) > 0
                        ? `${r.follow_me!.length} step${r.follow_me!.length === 1 ? '' : 's'}`
                        : <span className="arc-subtle">—</span>}
                    </td>
                    {/* Only meaningful where a no-answer rule exists: it is the
                        delay before that rule fires. */}
                    <td className="arc-td-num arc-muted">
                      {r.forward_no_answer || (r.follow_me?.length ?? 0) > 0
                        ? `${r.call_timeout_sec}s`
                        : <span className="arc-subtle">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!q.isLoading && rows.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add a seat first." />
        )}
      </div>

      {followMe.length > 0 && (
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 12 }}>
          {followMe.length} seat{followMe.length === 1 ? ' uses' : 's use'} follow-me.
          The steps are edited on the seat's own page.
        </p>
      )}
    </>
  );
}
